import { useEffect, useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import {
    AlertCircle, Ban, Calendar, CheckCircle2, Clock, Files, Loader2, Users, XCircle,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import {
    Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import type { VacationRequest, VacationRequestStatus } from "@/lib/collections/vacations/types";
import { approveVacationRequest, getRequestsByStatus, rejectVacationRequest } from "@/lib/collections/vacations/vacationsService";
import { getWorkerNames } from "@/lib/collections/vacations/vacationBalanceService";

const STATUSES: VacationRequestStatus[] = ["pending", "approved", "rejected", "cancelled"];

const STATUS_CONFIG: Record<
    VacationRequestStatus,
    { label: string; plural: string; icon: typeof AlertCircle; badge: string; iconBox: string; iconColor: string }
> = {
    pending: {
        label: "Pendiente", plural: "Pendientes", icon: AlertCircle,
        badge: "bg-yellow-100 text-yellow-800 border-yellow-200",
        iconBox: "bg-yellow-500/10", iconColor: "text-yellow-500",
    },
    approved: {
        label: "Aprobada", plural: "Aprobadas", icon: CheckCircle2,
        badge: "bg-green-100 text-green-800 border-green-200",
        iconBox: "bg-green-500/10", iconColor: "text-green-500",
    },
    rejected: {
        label: "Rechazada", plural: "Rechazadas", icon: XCircle,
        badge: "bg-red-100 text-red-800 border-red-200",
        iconBox: "bg-red-500/10", iconColor: "text-red-500",
    },
    cancelled: {
        label: "Cancelada", plural: "Canceladas", icon: Ban,
        badge: "bg-slate-100 text-slate-700 border-slate-200",
        iconBox: "bg-slate-500/10", iconColor: "text-slate-500",
    },
};

const fmt = (iso: string) =>
    new Date(iso + "T00:00:00").toLocaleDateString("es-EC", {
        day: "2-digit", month: "short", year: "numeric",
    });

export default function VacationApprovals() {
    const { user } = useAuth();
    const [status, setStatus] = useState<VacationRequestStatus>("pending");
    const [searchQuery, setSearchQuery] = useState("");
    const [allRequests, setAllRequests] = useState<VacationRequest[]>([]);
    const [names, setNames] = useState<Record<string, string>>({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [reloadKey, setReloadKey] = useState(0);

    const [actingId, setActingId] = useState<string | null>(null);
    const [actionError, setActionError] = useState<string | null>(null);

    const [rejecting, setRejecting] = useState<VacationRequest | null>(null);
    const [rejectReason, setRejectReason] = useState("");

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);

        Promise.all(STATUSES.map((s) => getRequestsByStatus(s)))
            .then(async (lists) => {
                const reqs = lists.flat();
                const nameMap = await getWorkerNames(reqs.map((r) => r.userId));
                if (cancelled) return;
                setAllRequests(reqs);
                setNames(nameMap);
            })
            .catch((e) => {
                console.error(e);
                if (!cancelled) setError("No se pudieron cargar las solicitudes.");
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [reloadKey]);

    const reload = () => setReloadKey((k) => k + 1);

    const counts = useMemo(() => {
        const c: Record<VacationRequestStatus, number> = { pending: 0, approved: 0, rejected: 0, cancelled: 0 };
        for (const r of allRequests) c[r.status]++;
        return c;
    }, [allRequests]);

    const visible = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        return allRequests.filter(
            (r) =>
                r.status === status &&
                (!q || (names[r.userId] ?? "").toLowerCase().includes(q) || (r.reason ?? "").toLowerCase().includes(q))
        );
    }, [allRequests, status, searchQuery, names]);

    const handleError = (e: unknown, fallback: string) => {
        console.error(e);
        setActionError(
            e instanceof Error && e.message === "NOT_PENDING"
                ? "Esa solicitud ya no está pendiente (quizá el empleado la canceló). Se recargó la lista."
                : fallback
        );
        reload();
    };

    const approve = async (request: VacationRequest) => {
        if (!request.$id || !user) return;
        setActingId(request.$id);
        setActionError(null);
        try {
            await approveVacationRequest(request.$id, user.$id);
            reload();
        } catch (e) {
            handleError(e, "No se pudo aprobar la solicitud.");
        } finally {
            setActingId(null);
        }
    };

    const reject = async () => {
        if (!rejecting?.$id || !user || !rejectReason.trim()) return;
        setActingId(rejecting.$id);
        setActionError(null);
        try {
            await rejectVacationRequest(rejecting.$id, user.$id, rejectReason);
            setRejecting(null);
            setRejectReason("");
            reload();
        } catch (e) {
            setRejecting(null);
            setRejectReason("");
            handleError(e, "No se pudo rechazar la solicitud.");
        } finally {
            setActingId(null);
        }
    };

    if (loading && allRequests.length === 0) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <>
            <Breadcrumb>
                <BreadcrumbList>
                    <BreadcrumbItem>
                        <div className="flex items-center gap-1.5">
                            <Files />
                            <BreadcrumbPage>Vacaciones</BreadcrumbPage>
                        </div>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem>
                        <BreadcrumbLink asChild>
                            <Link to="/requests">Menu</Link>
                        </BreadcrumbLink>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem>
                        <BreadcrumbPage>Administrativo - Vacaciones</BreadcrumbPage>
                    </BreadcrumbItem>
                </BreadcrumbList>
            </Breadcrumb>

            <div className="space-y-6 pt-6">
                <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
                    {STATUSES.map((s) => {
                        const cfg = STATUS_CONFIG[s];
                        const Icon = cfg.icon;
                        const active = status === s;
                        return (
                            <Card
                                key={s}
                                onClick={() => setStatus(s)}
                                className={`shadow-sm hover:shadow-md transition-shadow cursor-pointer border-2 ${active ? "border-primary" : "border-transparent"
                                    }`}
                            >

                                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                    <CardTitle className="text-sm font-medium">{cfg.plural}</CardTitle>
                                    <div className={`rounded-lg p-2 ${cfg.iconBox}`}>
                                        <Icon className={`h-4 w-4 ${cfg.iconColor}`} />
                                    </div>
                                </CardHeader>
                                <CardContent>
                                    <div className="text-2xl font-bold">{counts[s]}</div>
                                    <p className="text-xs text-muted-foreground mt-1">
                                        {allRequests.length > 0
                                            ? `${Math.round((counts[s] / allRequests.length) * 100)}% del total`
                                            : "0% del total"}
                                    </p>
                                </CardContent>
                            </Card>
                        );
                    })}
                </div>

                {/* Header + filtros */}
                <div className="flex flex-col gap-6">
                    <div>
                        <h2 className="text-3xl font-bold bg-linear-to-r from-primary to-primary/60 bg-clip-text text-transparent">
                            Solicitudes de vacaciones
                        </h2>
                        <p className="text-sm text-muted-foreground mt-1">
                            {visible.length} {visible.length === 1 ? "solicitud encontrada" : "solicitudes encontradas"}
                        </p>
                    </div>

                    <div className="w-full sm:max-w-md">
                        <Input
                            type="text"
                            placeholder="Buscar por colaborador o motivo..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                </div>

                {error && (
                    <Alert variant="destructive">
                        <AlertDescription>{error}</AlertDescription>
                    </Alert>
                )}
                {actionError && (
                    <Alert variant="destructive">
                        <AlertDescription>{actionError}</AlertDescription>
                    </Alert>
                )}

                {/* Grid */}
                {!error && visible.length === 0 ? (
                    <Card className="border-dashed">
                        <CardContent className="py-16 text-center">
                            <div className="flex flex-col items-center gap-4">
                                <div className="rounded-full bg-muted p-6">
                                    <Calendar className="h-12 w-12 text-muted-foreground" />
                                </div>
                                <div>
                                    <p className="text-lg font-medium">
                                        No hay solicitudes {STATUS_CONFIG[status].plural.toLowerCase()}
                                    </p>
                                    <p className="text-sm text-muted-foreground mt-1">
                                        {searchQuery ? "Intenta ajustar la búsqueda" : "No se encontraron solicitudes en este estado"}
                                    </p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                        {visible.map((r) => {
                            const cfg = STATUS_CONFIG[r.status];
                            const Icon = cfg.icon;
                            const busy = actingId === r.$id;
                            return (
                                <Card key={r.$id} className="hover:shadow-xl duration-300 overflow-hidden border-2">
                                    <CardHeader className="pb-0">
                                        <div className="flex justify-between items-start gap-2 mb-2">
                                            <div className="flex items-center gap-2 flex-1 min-w-0">
                                                <div className="rounded-lg bg-primary/10 p-2 shrink-0">
                                                    <Users className="h-4 w-4 text-primary" />
                                                </div>
                                                <h3 className="font-semibold text-base line-clamp-1 leading-tight">
                                                    {names[r.userId] || "Colaborador sin nombre"}
                                                </h3>
                                            </div>
                                            <Badge className={`${cfg.badge} border flex items-center gap-1`}>
                                                <Icon className="h-3 w-3" />
                                                {cfg.label}
                                            </Badge>
                                        </div>

                                        <p className="text-sm text-muted-foreground line-clamp-2 min-h-10 mt-2">
                                            {r.reason || "Sin motivo especificado"}
                                        </p>
                                    </CardHeader>

                                    <CardContent className="pt-1 pb-1 space-y-3">
                                        <div className="flex items-center gap-2 text-sm">
                                            <div className="rounded-md bg-blue-500/10 p-1.5">
                                                <Calendar className="h-3.5 w-3.5 text-blue-600" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-xs text-muted-foreground">Período</p>
                                                <p className="font-medium">{fmt(r.startDate)} – {fmt(r.endDate)}</p>
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-2 text-sm">
                                            <div className="rounded-md bg-purple-500/10 p-1.5">
                                                <Clock className="h-3.5 w-3.5 text-purple-600" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-xs text-muted-foreground">Duración</p>
                                                <p className="font-medium">
                                                    {r.daysDeducted} descontados · {r.calendarDays} calendario
                                                </p>
                                            </div>
                                        </div>

                                        {r.status === "rejected" && r.rejectionReason && (
                                            <p className="wrap-break-word text-sm text-destructive">
                                                Motivo del rechazo: {r.rejectionReason}
                                            </p>
                                        )}

                                        {r.status === "pending" && (
                                            <div className="pt-2 border-t flex justify-end gap-2">
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    className="cursor-pointer"
                                                    disabled={busy}
                                                    onClick={() => {
                                                        setActionError(null);
                                                        setRejectReason("");
                                                        setRejecting(r);
                                                    }}
                                                >
                                                    Rechazar
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    className="cursor-pointer"
                                                    disabled={busy}
                                                    onClick={() => approve(r)}
                                                >
                                                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Aprobar"}
                                                </Button>
                                            </div>
                                        )}
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </div>
                )}

                {/* Dialog de rechazo */}
                <Dialog
                    open={!!rejecting}
                    onOpenChange={(open) => {
                        if (!open && !actingId) setRejecting(null);
                    }}
                >
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>Rechazar solicitud</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-2">
                            <p className="text-sm text-muted-foreground">Indica el motivo: lo verá el colaborador.</p>
                            <Textarea
                                value={rejectReason}
                                maxLength={500}
                                onChange={(e) => setRejectReason(e.target.value)}
                                placeholder="Ej: Esas fechas coinciden con el cierre de inventario"
                            />
                        </div>
                        <DialogFooter>
                            <Button
                                variant="destructive"
                                disabled={!rejectReason.trim() || !!actingId}
                                onClick={reject}
                            >
                                {actingId ? "Rechazando..." : "Rechazar"}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
        </>
    );
}